import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import AssistantCitationSchema from "ai-assistant/citations/assistant-citation-schema";
import { z } from "zod";

export type AssistantConversationTurnJSON = z.infer<typeof AssistantConversationSchema.AssistantConversationTurnJSONZ>;
export type AssistantConversationTurnParams = z.infer<typeof AssistantConversationSchema.AssistantConversationTurnZ>;
export type AssistantConversationJSON = z.infer<typeof AssistantConversationSchema.AssistantConversationJSONZ>;
export type AssistantConversationParams = z.infer<typeof AssistantConversationSchema.AssistantConversationZ>;

/**
 * A conversation read back from the server.
 *
 * The transcript used to live in this component's React state and nowhere the user could reach,
 * so a refresh lost it while the server kept every turn indefinitely. See #4658.
 *
 * The citations arriving here are not the citations that were sent when the answer was written:
 * the server re-checks each one against what the reader may see now, and one they may no longer
 * read arrives with `withheld: true` and no text.
 */
export default class AssistantConversationSchema {
  public static readonly AssistantConversationTurnJSONZ = z.object({
    sequence: z.number(),
    userMessage: z.string(),
    answerText: z.string(),
    createdAt: z.iso.datetime({ offset: true }),
    citations: z.array(AssistantCitationSchema.AssistantCitationJSONZ),
  });

  public static readonly AssistantConversationTurnZ = z.object({
    sequence: z.number(),
    userMessage: z.string(),
    answerText: z.string(),
    createdAt: z.custom<AHDate>(),
    citations: z.array(AssistantCitationSchema.AssistantCitationZ),
  });

  public static readonly AssistantConversationJSONZ = z.object({
    conversationId: z.uuid(),
    startedAt: z.iso.datetime({ offset: true }),
    lastMessageAt: z.iso.datetime({ offset: true }),
    turns: z.array(AssistantConversationSchema.AssistantConversationTurnJSONZ),
    // Whether anything was prepared for approval here. The previews themselves are not in
    // `turns` and are not meant to be - but the answers announcing them are, so this is what
    // lets a replayed transcript say that "confirm it below" no longer has anything below it.
    // Absent on a reply from an API that predates the field; absent reads as false, which is
    // the same thing it meant before the field existed.
    hadPreparedActions: z.boolean().nullish(),
  });

  public static readonly AssistantConversationZ = z.object({
    conversationId: z.string(),
    startedAt: z.custom<AHDate>(),
    lastMessageAt: z.custom<AHDate>(),
    turns: z.array(AssistantConversationSchema.AssistantConversationTurnZ),
    hadPreparedActions: z.boolean().optional(),
  });

  public static validate(values: unknown): AssistantConversationParams {
    return parseOrThrow(AssistantConversationSchema.AssistantConversationZ, values, {
      logError: true,
      schemaName: "AssistantConversationSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is AssistantConversationParams {
    try {
      AssistantConversationSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: AssistantConversationJSON): AssistantConversationParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(AssistantConversationSchema.AssistantConversationJSONZ, json, {
      logError: true,
      schemaName: "AssistantConversationSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return {
      conversationId: parsed.conversationId,
      startedAt: new AHDate(parsed.startedAt),
      lastMessageAt: new AHDate(parsed.lastMessageAt),
      turns: parsed.turns.map((turn) => ({
        sequence: turn.sequence,
        userMessage: turn.userMessage,
        answerText: turn.answerText,
        createdAt: new AHDate(turn.createdAt),
        citations: turn.citations.map((citation) => AssistantCitationSchema.deserialize(citation)),
      })),
      hadPreparedActions: parsed.hadPreparedActions ?? false,
    };
  }
}
